import { action, computed, makeObservable, observable } from 'mobx';
import type { RootStore } from '.';
import { Card } from './card';
import { Source } from './source';
import { UiSourceModulePowerColumn } from './ui-source-module-power-column';

export enum FilterMode {
  OR = 'OR',
  AND = 'AND',
}

export class UiModulePowerColumnStore {
  private rootStore: RootStore;
  sourceModulePowerColumns: Record<string, UiSourceModulePowerColumn> = {};
  showDetailedCounts: boolean = false;
  filterMode: FilterMode = FilterMode.AND;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable<this, 'sourceModulePowerColumns'>(this, {
      sourceModulePowerColumns: observable,
      rowSelectionsByModuleId: computed,
      showDetailedCounts: observable,
      filterMode: observable,
      initializeColumns: action,
      hasAnyPowerColumnFilters: computed,
      setFilterMode: action.bound,
    });
  }

  get hasAnyPowerColumnFilters() {
    return Object.keys(this.rowSelectionsByModuleId).length > 0;
  }

  setFilterMode(mode: FilterMode) {
    this.filterMode = mode;
  }

  initializeColumns(sources: Source[]) {
    sources.forEach((source) => {
      if (!this.sourceModulePowerColumns[source.id]) {
        this.sourceModulePowerColumns[source.id] = new UiSourceModulePowerColumn(
          this.rootStore,
          source.id,
          this.rootStore.sessionsStore.currentSession!,
        );
      }
    });
  }

  getSourceModulePowerColumn = (sourceId: string) =>
    this.sourceModulePowerColumns[sourceId] || null;

  get rowSelectionsByModuleId() {
    const rowSelectionsByModuleId: Record<string, Set<string>> = {};

    Object.values(this.sourceModulePowerColumns).forEach((pc) => {
      Object.entries(pc.selectedRowsByModule).forEach(([moduleId, rows]) => {
        if (rows.size > 0) {
          rowSelectionsByModuleId[moduleId] = rows;
        }
      });
    });

    return rowSelectionsByModuleId;
  }

  clearAllModuleFilters = () => {
    for (const spc of Object.values(this.sourceModulePowerColumns)) {
      spc.clearAllFilters();
    }
  };

  clearModuleFilters = (moduleId: string) => {
    const sourceId = this.rootStore.sessionsStore.currentSession?.data.modules[moduleId].sourceId;
    if (sourceId) {
      this.sourceModulePowerColumns[sourceId].clearFilters(moduleId);
    }
  };

  isRowSelected = (moduleId: string, rowId: string) =>
    !!this.rowSelectionsByModuleId[moduleId]?.has(rowId);

  get hasRowSelection() {
    return Object.values(this.rowSelectionsByModuleId).some((sel) => sel.size > 0);
  }

  isCardVisibleBasedOnRowSelections = (card: Card) => {
    if (!this.hasRowSelection) {
      return true;
    }

    const activeModules = Object.keys(this.rowSelectionsByModuleId);

    if (this.filterMode === FilterMode.OR) {
      // OR logic: card is visible if it matches ANY of the active filters
      return activeModules.some((moduleId) => {
        const raw = card.data.connections[moduleId];
        const cardRows: string[] = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
        const selectedRows = Array.from(
          this.rowSelectionsByModuleId[moduleId] || new Set<string>(),
        );

        // Within module OR: card must have ANY of the selected rows
        return (
          cardRows.length > 0 && selectedRows.some((selectedRow) => cardRows.includes(selectedRow))
        );
      });
    }
    // AND logic: card is visible only if it matches ALL of the active filters
    return activeModules.every((moduleId) => {
      const raw = card.data.connections[moduleId];
      const cardRows: string[] = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
      const selectedRows = Array.from(this.rowSelectionsByModuleId[moduleId] || new Set<string>());

      // Within module AND: card must have ALL of the selected rows
      return (
        cardRows.length > 0 && selectedRows.every((selectedRow) => cardRows.includes(selectedRow))
      );
    });
  };
}
